import { useId, useState } from "react";
import { annualDistance, compute, costItem, fixedAnnual, fixedPerDistance } from "../calc";
import {
  DECIMALS_MONEY,
  ITEM_PERIODS,
  MAX_COST_NAME,
  MAX_ECONOMY,
  MAX_PRICE,
  MAX_VEHICLE_NAME,
  MINOR_PER_MAJOR,
} from "../constants";
import { help, itemPeriodLabel, profilePanel, sections, vehicleForm as copy } from "../content";
import { useFmt } from "../fmt";
import type { CostItem, EconomyKey, FuelType, ItemPeriod, Profile, Vehicle } from "../types";
import { HelpTip } from "./HelpTip";
import { NumberInput } from "./NumberInput";

const FUEL_ICON = (
  <svg
    className="fuel-icon"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16" />
    <path d="M3 10h8" />
    <path d="M14 8l3 3v6a1.5 1.5 0 0 0 3 0v-5l-2-2" />
    <path d="M3 21h11" />
  </svg>
);
const ELECTRIC_ICON = (
  <svg className="fuel-icon" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
  </svg>
);
const TRASH_ICON = (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
  </svg>
);
const FUEL_ICONS: Record<FuelType, typeof FUEL_ICON> = { ice: FUEL_ICON, ev: ELECTRIC_ICON };

interface Props {
  vehicle: Vehicle;
  profile: Profile;
  onChange: (patch: Partial<Vehicle>) => void;
}

export function VehicleForm({ vehicle: v, profile: p, onChange }: Props) {
  const fmt = useFmt();
  const headingId = useId();
  const fieldId = useId();
  const [focusItemId, setFocusItemId] = useState<string | null>(null);
  const review = v.review;

  const fields: EconomyKey[] = v.fuel === "ice" ? ["mpg", "iceHomePrice", "iceAwayPrice"] : ["evMiPerKwh", "evHomeRate", "evAwayRate"];
  // Only counts what's on screen: an unedited EV default while set to petrol shouldn't keep the note showing.
  const hasUnreviewed = !!review && (fields.some((f) => review.fields.includes(f)) || review.items.length > 0);

  const clearField = (key: EconomyKey): Partial<Vehicle> =>
    review ? { review: { ...review, fields: review.fields.filter((f) => f !== key) } } : {};

  const setNum = (key: EconomyKey) => (n: number) => onChange({ [key]: n, ...clearField(key) });

  const setItem = (id: string, patch: Partial<CostItem>) =>
    onChange({
      costItems: v.costItems.map((i) => (i.id === id ? { ...i, ...patch } : i)),
      ...(patch.amount !== undefined && review
        ? { review: { ...review, items: review.items.filter((x) => x !== id) } }
        : {}),
    });

  const fixed = fixedAnnual(v, p);
  const fixedText = `${fmt.money(fixed)}/yr · ${fmt.rate(fixedPerDistance(v, p))}`;
  const energy = compute(v, p, fmt.unit);
  const fuelText = `${fmt.money((energy.energyPpm / MINOR_PER_MAJOR) * annualDistance(p))}/yr · ${fmt.rate(energy.energyPpm)}`;

  const isEv = v.fuel === "ev";
  const [homePriceKey, awayPriceKey, economyKey]: EconomyKey[] = isEv
    ? ["evHomeRate", "evAwayRate", "evMiPerKwh"]
    : ["iceHomePrice", "iceAwayPrice", "mpg"];
  const priceUnit = `${fmt.minor}/${isEv ? "kWh" : "L"}`;
  const economyUnit = isEv ? `${fmt.unit}/kWh` : fmt.unit === "mi" ? "mpg" : "km/L";

  const numField = (key: EconomyKey, label: string, unit: string, max: number) => {
    const id = `${fieldId}-${key}`;
    return (
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <div className="input-wrap">
          <NumberInput
            id={id}
            value={v[key]}
            decimals={DECIMALS_MONEY}
            max={max}
            review={review?.fields.includes(key)}
            onChange={setNum(key)}
          />
          <span className="unit">{unit}</span>
        </div>
      </div>
    );
  };

  return (
    <section className="panel" aria-labelledby={headingId}>
      <h3 className="sr-only" id={headingId}>
        {v.name || copy.fallbackName}
      </h3>
      <div className="title-row">
        <input
          type="text"
          value={v.name}
          maxLength={MAX_VEHICLE_NAME}
          placeholder={copy.namePlaceholder}
          aria-label={copy.nameLabel}
          onChange={(e) => onChange({ name: e.target.value })}
        />
        {v.isExample && <span className="example-tag">{copy.exampleTag}</span>}
      </div>
      <p className="review-note" hidden={!hasUnreviewed}>
        {copy.reviewNote}
      </p>
      <p className="sub profile-line">
        <span className="ellipsis" title={p.name}>
          {p.name || profilePanel.fallbackName}
        </span>
        <span className="meta">({fmt.profileMeta(p)})</span>
      </p>

      <h4 className="eyebrow section-label">{sections.fixedCosts}</h4>
      <ul className="worksheet">
        {v.costItems.map((item) => {
          const isDist = item.period === "distance";
          return (
            <li className="ws-row" key={item.id}>
              {/* Only ever true for a row the person just added with "+ Add a cost", so
                  focus follows the new row instead of staying on the button. */}
              <input
                className="ws-name"
                type="text"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={focusItemId === item.id}
                value={item.name}
                maxLength={MAX_COST_NAME}
                placeholder={copy.costNamePlaceholder}
                aria-label={copy.costNameLabel}
                onChange={(e) => setItem(item.id, { name: e.target.value })}
              />
              <span className="ws-lit">{isDist ? fmt.minor : fmt.symbol}</span>
              <NumberInput
                className="ws-amount"
                value={item.amount}
                decimals={DECIMALS_MONEY}
                label={copy.amountFor(item.name)}
                review={review?.items.includes(item.id)}
                onChange={(n) => setItem(item.id, { amount: n })}
              />
              <span className="ws-lit">{copy.perLabel}</span>
              <select
                className="ws-period"
                aria-label={copy.periodFor(item.name)}
                value={item.period}
                onChange={(e) => setItem(item.id, { period: e.target.value as ItemPeriod })}
              >
                {ITEM_PERIODS.map((per) => (
                  <option key={per} value={per}>
                    {itemPeriodLabel(per, fmt.unit)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="ws-remove"
                title={copy.removeCost}
                aria-label={copy.removeCostNamed(item.name)}
                onClick={() => onChange({ costItems: v.costItems.filter((i) => i.id !== item.id) })}
              >
                {TRASH_ICON}
              </button>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="btn-add add-cost"
        onClick={() => {
          const added = costItem("", 0, "year");
          setFocusItemId(added.id);
          onChange({ costItems: [...v.costItems, added] });
        }}
      >
        + {copy.addCost}
      </button>
      <p className="ws-total">
        <span>
          {copy.subTotal}
          <HelpTip label={help.fixedCosts.label}>{help.fixedCosts.body(fmt.unit)}</HelpTip>
        </span>
        <b>{fixedText}</b>
      </p>

      <h4 className="eyebrow section-label">{sections.fuelCosts}</h4>
      <fieldset className="segmented fuel-toggle">
        <legend className="sr-only">{copy.fuelTypeLabel}</legend>
        {(["ice", "ev"] as const).map((fuel) => (
          <label key={fuel}>
            <input
              type="radio"
              name={`fuel-${v.id}`}
              value={fuel}
              checked={v.fuel === fuel}
              onChange={() => onChange({ fuel })}
            />
            {FUEL_ICONS[fuel]}
            {copy.fuelOptions[fuel]}
          </label>
        ))}
      </fieldset>
      <div className="field-grid">
        {numField(homePriceKey, copy.homePrice, priceUnit, MAX_PRICE)}
        {numField(awayPriceKey, copy.awayPrice, priceUnit, MAX_PRICE)}
        {numField(economyKey, copy.economy, economyUnit, MAX_ECONOMY)}
      </div>
      <p className="ws-total">
        <span>
          {copy.subTotal}
          <HelpTip label={help.fuelCosts.label}>{help.fuelCosts.body(v.fuel, fmt.unit)}</HelpTip>
        </span>
        <b>{fuelText}</b>
      </p>
    </section>
  );
}
